import { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { FileXls, CheckCircle, XCircle, MinusCircle, UsersThree, CurrencyInr, UploadSimple, Funnel, FunnelSimple, X, ClipboardText, CalendarBlank } from "@phosphor-icons/react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Input } from "@/components/ui/input";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { ZohoCard } from "@/components/ZohoConnect";
import { CapiCard } from "@/components/CapiCard";
import { api } from "@/lib/api";

const POSITIVE_HINTS = ["converted", "enrolled", "paid", "admission", "demo scheduled", "won", "closed won", "positive"];

const parseDate = (s) => new Date(String(s || "").replace("+0000", "+00:00"));
const fmtDT = (s) => {
  const d = parseDate(s);
  if (isNaN(d)) return "—";
  return d.toLocaleString("en-IN", { day: "numeric", month: "short" }) + " · " + d.toLocaleTimeString("en-IN", { hour: "2-digit", minute: "2-digit" });
};

const StatusChip = ({ status, positive }) => {
  if (!status) return <span className="rounded-full bg-[#F3F4F6] px-2.5 py-0.5 text-[10px] font-bold text-[#8a90a0]">No sales data</span>;
  return (
    <span className={`rounded-full px-2.5 py-0.5 text-[10px] font-bold ${
      positive === true ? "bg-success/10 text-[#00953e]" : positive === false ? "bg-danger/10 text-[#c73030]" : "bg-warning/10 text-[#b07600]"}`}>
      {status}
    </span>
  );
};

const PositiveMark = ({ value, onToggle }) => (
  <button onClick={onToggle} className="inline-flex items-center" title="✓ positive → ✗ negative → — unset">
    {value === true ? <CheckCircle size={22} weight="fill" className="text-[#00953e]" />
      : value === false ? <XCircle size={22} weight="fill" className="text-[#c73030]/70" />
      : <MinusCircle size={22} weight="duotone" className="text-[#c9cdd6]" />}
  </button>
);

const Stat = ({ icon: Icon, label, value, sub, accent, testId }) => (
  <div className="bg-white border border-[#E5E7EB] rounded-lg p-4" data-testid={testId}>
    <div className="flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-[#8a90a0]"><Icon size={13} weight="duotone" className={accent ? "text-[#FF6B35]" : "text-[#0052FF]"} /> {label}</div>
    <div className="mt-1.5 font-heading text-xl font-extrabold tracking-tighter text-[#0A0D14]">{value}</div>
    {sub && <div className="text-[11px] text-[#8a90a0] mt-0.5">{sub}</div>}
  </div>
);

const FSelect = ({ value, onChange, options, all, width = "w-44", testId }) => (
  <Select value={value} onValueChange={onChange}>
    <SelectTrigger className={`${width} h-9 text-xs font-semibold bg-white`} data-testid={testId}><SelectValue /></SelectTrigger>
    <SelectContent>
      <SelectItem value="all">{all}</SelectItem>
      {options.map((o) => <SelectItem key={o.v} value={o.v}>{o.l}</SelectItem>)}
    </SelectContent>
  </Select>
);

const ValuePills = ({ values, selected, onToggle, tone }) => (
  <div className="flex flex-wrap gap-1.5 mt-1.5">
    {values.map((v) => (
      <button key={v} onClick={() => onToggle(v)}
        className={`rounded-full border px-2.5 py-1 text-[10px] font-bold transition-colors ${
          selected.includes(v)
            ? tone === "pos" ? "border-[#00953e] bg-success/10 text-[#00953e]" : "border-[#c73030] bg-danger/10 text-[#c73030]"
            : "border-[#E5E7EB] text-[#525866] hover:bg-[#F3F4F6]"}`}>
        {v}
      </button>
    ))}
  </div>
);

function MappingDialog({ upload, open, onClose, onDone }) {
  const [matchCol, setMatchCol] = useState("");
  const [matchType, setMatchType] = useState("phone");
  const [statusCol, setStatusCol] = useState("");
  const [posVals, setPosVals] = useState([]);
  const [negVals, setNegVals] = useState([]);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    if (!upload) return;
    const cols = upload.columns || [];
    const guessMatch = cols.find((c) => /phone|mobile|number|contact/i.test(c)) || cols.find((c) => /email/i.test(c)) || "";
    setMatchType(/email/i.test(guessMatch) ? "email" : "phone");
    setMatchCol(guessMatch);
    const guessStatus = cols.find((c) => /status|remark|stage|outcome|disposition/i.test(c)) || "";
    setStatusCol(guessStatus);
    const dv = guessStatus ? (upload.distinct[guessStatus] || []) : [];
    setPosVals(dv.filter((v) => POSITIVE_HINTS.some((h) => v.toLowerCase().includes(h))));
    setNegVals([]);
  }, [upload]);

  if (!upload) return null;
  const statusValues = statusCol ? (upload.distinct[statusCol] || []) : [];

  const commit = async () => {
    if (!matchCol || !statusCol) { toast.error("Pick the match column and the status column"); return; }
    if (posVals.length === 0) { toast.error("Select at least one status value that counts as positive"); return; }
    setBusy(true);
    try {
      const { data } = await api.post("/assessment/commit", {
        upload_id: upload.upload_id, match_column: matchCol, match_type: matchType,
        status_column: statusCol, positive_values: posVals, negative_values: negVals,
      });
      toast.success(`Matched ${data.matched} of ${data.rows} rows with your Facebook leads`);
      onDone();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Could not process the sheet", { duration: 8000 });
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => !v && onClose()}>
      <DialogContent className="max-w-lg" data-testid="mapping-dialog">
        <DialogHeader><DialogTitle className="font-heading font-extrabold">Map your sheet columns</DialogTitle></DialogHeader>
        <div className="space-y-4 text-sm">
          <p className="text-xs text-[#8a90a0]">{upload.row_count} rows found. Tell us which column identifies the lead and which holds your sales status.</p>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-xs font-bold text-[#0A0D14]">Match leads by</label>
              <Select value={matchType} onValueChange={setMatchType}>
                <SelectTrigger className="mt-1 h-9 text-xs font-semibold" data-testid="map-match-type"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value="phone">Phone number</SelectItem><SelectItem value="email">Email</SelectItem></SelectContent>
              </Select>
            </div>
            <div className="flex-1">
              <label className="text-xs font-bold text-[#0A0D14]">Column with {matchType}</label>
              <Select value={matchCol} onValueChange={setMatchCol}>
                <SelectTrigger className="mt-1 h-9 text-xs font-semibold" data-testid="map-match-col"><SelectValue placeholder="Pick column" /></SelectTrigger>
                <SelectContent>{upload.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>
          <div>
            <label className="text-xs font-bold text-[#0A0D14]">Status column (the one variable your sales team fills)</label>
            <Select value={statusCol} onValueChange={(v) => { setStatusCol(v); setPosVals([]); setNegVals([]); }}>
              <SelectTrigger className="mt-1 h-9 text-xs font-semibold" data-testid="map-status-col"><SelectValue placeholder="Pick column" /></SelectTrigger>
              <SelectContent>{upload.columns.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
            </Select>
          </div>
          {statusValues.length > 0 && (
            <>
              <div>
                <label className="text-xs font-bold text-[#00953e]">✓ Positive when status is:</label>
                <ValuePills values={statusValues} selected={posVals} tone="pos"
                  onToggle={(v) => { setPosVals((p) => p.includes(v) ? p.filter((x) => x !== v) : [...p, v]); setNegVals((n) => n.filter((x) => x !== v)); }} />
              </div>
              <div>
                <label className="text-xs font-bold text-[#c73030]">✗ Negative when status is: <span className="text-[#8a90a0] font-semibold">(optional — rest stay "in follow-up")</span></label>
                <ValuePills values={statusValues.filter((v) => !posVals.includes(v))} selected={negVals} tone="neg"
                  onToggle={(v) => setNegVals((n) => n.includes(v) ? n.filter((x) => x !== v) : [...n, v])} />
              </div>
            </>
          )}
          <Button onClick={commit} disabled={busy} className="w-full rounded-full bg-[#0A0D14] hover:bg-black text-white font-bold h-10" data-testid="map-commit-btn">
            {busy ? "Matching…" : "Match with Facebook leads"}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
}

export default function LeadAssessment() {
  const [rows, setRows] = useState(null);
  const [cpl, setCpl] = useState({});
  const [sheet, setSheet] = useState({});
  const [formNames, setFormNames] = useState({});
  const [upload, setUpload] = useState(null);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);

  const [fCamp, setFCamp] = useState("all");
  const [fForm, setFForm] = useState("all");
  const [fStatus, setFStatus] = useState("all");
  const [fSource, setFSource] = useState("all");
  const [fPositive, setFPositive] = useState("all");
  const [fDays, setFDays] = useState("all");
  const [fFrom, setFFrom] = useState("");
  const [fTo, setFTo] = useState("");
  const [q, setQ] = useState("");

  const load = () => {
    api.get("/assessment/leads").then(({ data }) => {
      setRows(data.leads); setCpl(data.campaign_cpl || {}); setSheet(data.sheet_settings || {});
    }).catch(() => setRows([]));
  };
  useEffect(() => {
    load();
    api.get("/forms").then(({ data }) => setFormNames(Object.fromEntries((data.forms || []).map((f) => [f.id, f.name])))).catch(() => {});
  }, []);

  const pickFile = () => fileRef.current?.click();
  const onFile = async (e) => {
    const f = e.target.files?.[0];
    e.target.value = "";
    if (!f) return;
    setUploading(true);
    try {
      const fd = new FormData();
      fd.append("file", f);
      const { data } = await api.post("/assessment/upload", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setUpload(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || "Upload failed", { duration: 8000 });
    } finally { setUploading(false); }
  };

  const mark = async (lead) => {
    const next = lead.positive === true ? false : lead.positive === false ? null : true;
    setRows((rs) => rs.map((r) => (r.id === lead.id ? { ...r, positive: next, matched: true, outcome_source: next === null ? r.outcome_source : "manual" } : r)));
    try {
      await api.put(`/assessment/leads/${lead.id}/mark`, { positive: next });
    } catch { toast.error("Could not save the mark"); load(); }
  };

  const campaigns = useMemo(() => [...new Set((rows || []).map((l) => l.campaign_name).filter(Boolean))], [rows]);
  const forms = useMemo(() => [...new Set((rows || []).map((l) => l.form_id).filter(Boolean))], [rows]);
  const statuses = useMemo(() => [...new Set((rows || []).map((l) => l.sales_status).filter(Boolean))], [rows]);

  const dateRange = useMemo(() => {
    const now = new Date();
    const sod = new Date(now.getFullYear(), now.getMonth(), now.getDate());
    const monday = new Date(sod.getTime() - ((sod.getDay() + 6) % 7) * 86400000);
    if (fDays === "all") return [null, null];
    if (fDays === "today") return [sod, null];
    if (fDays === "yesterday") return [new Date(sod.getTime() - 86400000), sod];
    if (fDays === "this_week") return [monday, null];
    if (fDays === "prev_week") return [new Date(monday.getTime() - 7 * 86400000), monday];
    if (fDays === "this_month") return [new Date(now.getFullYear(), now.getMonth(), 1), null];
    if (fDays === "prev_month") return [new Date(now.getFullYear(), now.getMonth() - 1, 1), new Date(now.getFullYear(), now.getMonth(), 1)];
    if (fDays === "custom") return [fFrom ? new Date(fFrom) : null, fTo ? new Date(new Date(fTo).getTime() + 86400000) : null];
    return [new Date(Date.now() - Number(fDays) * 86400000), null];
  }, [fDays, fFrom, fTo]);

  const shown = useMemo(() => {
    const [start, end] = dateRange;
    return (rows || []).filter((l) => {
      const d = parseDate(l.created_at);
      return (fCamp === "all" || l.campaign_name === fCamp) &&
        (fForm === "all" || l.form_id === fForm) &&
        (fStatus === "all" || (fStatus === "none" ? !l.sales_status : l.sales_status === fStatus)) &&
        (fSource === "all" || (fSource === "unmatched" ? !l.matched : l.outcome_source === fSource)) &&
        (fPositive === "all" || (fPositive === "positive" ? l.positive === true : fPositive === "negative" ? l.positive === false : (l.positive === null || l.positive === undefined))) &&
        (!start || d >= start) && (!end || d < end) &&
        (!q || (l.name || "").toLowerCase().includes(q.toLowerCase()) || (l.phone || "").includes(q));
    });
  }, [rows, fCamp, fForm, fStatus, fSource, fPositive, dateRange, q]);

  const stats = useMemo(() => {
    const matched = shown.filter((l) => l.matched).length;
    const pos = shown.filter((l) => l.positive === true).length;
    const neg = shown.filter((l) => l.positive === false).length;
    const pending = shown.filter((l) => l.matched && (l.positive === null || l.positive === undefined)).length;
    const spend = shown.reduce((s, l) => s + (cpl[l.campaign_name] || 0), 0);
    return { total: shown.length, matched, pos, neg, pending, spend,
             matchPct: shown.length ? Math.round((matched / shown.length) * 100) : 0,
             convPct: matched ? Math.round((pos / matched) * 100) : 0,
             cpp: pos ? Math.round(spend / pos) : 0 };
  }, [shown, cpl]);

  const activeFilters = [fCamp, fForm, fStatus, fSource, fPositive].filter((f) => f !== "all").length + (fDays !== "all" ? 1 : 0) + (q ? 1 : 0);
  const clearAll = () => { setFCamp("all"); setFForm("all"); setFStatus("all"); setFSource("all"); setFPositive("all"); setFDays("all"); setFFrom(""); setFTo(""); setQ(""); };
  const daysLabel = { all: "all time", 7: "last 7 days", 14: "last 14 days", 30: "last 30 days",
                      today: "today", yesterday: "yesterday", this_week: "this week", prev_week: "previous week",
                      this_month: "this month", prev_month: "previous month", custom: "custom range" }[fDays] || "all time";
  const formName = (id) => (id ? (formNames[id] || `Form …${String(id).slice(-4)}`) : "—");

  return (
    <div className="space-y-6" data-testid="lead-assessment-page">
      <input ref={fileRef} type="file" accept=".csv,.xlsx,.xls" className="hidden" onChange={onFile} data-testid="sheet-file-input" />
      <MappingDialog upload={upload} open={!!upload} onClose={() => setUpload(null)} onDone={() => { setUpload(null); load(); }} />

      <div className="grid grid-cols-1 md:grid-cols-2 xl:grid-cols-3 gap-4">
        <div className="rounded-xl border border-[#E5E7EB] bg-white p-5" data-testid="sheet-source-card">
          <div className="flex items-center gap-3">
            <span className="h-10 w-10 rounded-lg bg-[#0F9D58]/10 flex items-center justify-center"><FileXls size={20} weight="duotone" className="text-[#0F9D58]" /></span>
            <div className="mr-auto">
              <div className="font-bold text-sm text-[#0A0D14]">Google Sheet / CSV upload</div>
              <div className="text-[11px] text-[#8a90a0]">
                {sheet.last_upload_at
                  ? `Last upload: ${fmtDT(sheet.last_upload_at)} · ${sheet.rows} rows · ${sheet.matched} matched by ${sheet.match_type}`
                  : "Export your sales follow-up sheet and upload — needs a phone/email column + a status column"}
              </div>
            </div>
            <button onClick={pickFile} disabled={uploading} data-testid="sheet-upload-btn"
              className="inline-flex items-center gap-1.5 rounded-full bg-[#0A0D14] text-white text-[11px] font-bold px-3.5 py-2 hover:bg-black disabled:opacity-50">
              <UploadSimple size={13} weight="bold" /> {uploading ? "Reading…" : sheet.last_upload_at ? "Upload again" : "Upload sheet"}
            </button>
          </div>
          {sheet.positive_values?.length > 0 && (
            <p className="text-[11px] text-[#8a90a0] mt-3">Status column "{sheet.status_column}" · positive: <b className="text-[#00953e]">{sheet.positive_values.join(", ")}</b>{sheet.negative_values?.length ? <> · negative: <b className="text-[#c73030]">{sheet.negative_values.join(", ")}</b></> : null}</p>
          )}
        </div>
        <ZohoCard onSynced={load} />
        <CapiCard />
      </div>

      <div className="bg-white border border-[#E5E7EB] rounded-xl p-4" data-testid="assessment-filter-bar">
        <div className="flex items-center gap-2 flex-wrap">
          <span className="inline-flex items-center gap-1.5 text-[11px] font-bold text-[#0A0D14] mr-1"><FunnelSimple size={15} weight="bold" /> Filters</span>
          <FSelect value={fCamp} onChange={setFCamp} all="All campaigns" options={campaigns.map((c) => ({ v: c, l: c }))} width="w-52" testId="filter-campaign" />
          <FSelect value={fForm} onChange={setFForm} all="All Instant Forms" options={forms.map((f) => ({ v: f, l: formName(f) }))} width="w-48" testId="filter-form" />
          <FSelect value={fStatus} onChange={setFStatus} all="All sales statuses"
            options={[...statuses.map((s) => ({ v: s, l: s })), { v: "none", l: "No sales data" }]} testId="filter-status" />
          <FSelect value={fSource} onChange={setFSource} all="All sources"
            options={[{ v: "sheet", l: "Sheet upload" }, { v: "zoho", l: "Zoho CRM" }, { v: "manual", l: "Manual mark" }, { v: "unmatched", l: "Unmatched" }]} width="w-36" testId="filter-source" />
          <FSelect value={fPositive} onChange={setFPositive} all="Any outcome"
            options={[{ v: "positive", l: "✓ Positive" }, { v: "negative", l: "✗ Negative" }, { v: "unset", l: "— Undecided" }]} width="w-36" testId="filter-positive" />
          <FSelect value={fDays} onChange={setFDays} all="All time"
            options={[{ v: "today", l: "Today" }, { v: "yesterday", l: "Yesterday" },
                      { v: "this_week", l: "This week" }, { v: "prev_week", l: "Previous week" },
                      { v: "7", l: "Last 7 days" }, { v: "14", l: "Last 14 days" }, { v: "30", l: "Last 30 days" },
                      { v: "this_month", l: "This month" }, { v: "prev_month", l: "Previous month" }, { v: "custom", l: "Custom range…" }]}
            width="w-40" testId="filter-days" />
          {fDays === "custom" && (
            <div className="flex items-center gap-1.5" data-testid="custom-date-range">
              <input type="date" value={fFrom} onChange={(e) => setFFrom(e.target.value)} data-testid="filter-date-from"
                className="h-9 rounded-md border border-input bg-white px-2 text-xs font-semibold text-[#0A0D14]" />
              <span className="text-[11px] text-[#8a90a0] font-bold">to</span>
              <input type="date" value={fTo} onChange={(e) => setFTo(e.target.value)} data-testid="filter-date-to"
                className="h-9 rounded-md border border-input bg-white px-2 text-xs font-semibold text-[#0A0D14]" />
            </div>
          )}
          <Input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name / phone"
            className="h-9 w-44 text-xs bg-white" data-testid="filter-search" />
          {activeFilters > 0 && (
            <button onClick={clearAll} className="inline-flex items-center gap-1 rounded-full bg-[#F3F4F6] text-[11px] font-bold text-[#525866] px-3 py-1.5 hover:bg-[#e7e9ee]" data-testid="filter-clear">
              <X size={11} weight="bold" /> Clear ({activeFilters})
            </button>
          )}
        </div>
      </div>

      <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-3" data-testid="assessment-stat-cards">
        <Stat icon={UsersThree} label="Leads" value={stats.total} sub={daysLabel} testId="stat-leads" />
        <Stat icon={Funnel} label="Matched" value={`${stats.matched} (${stats.matchPct}%)`} sub={`${stats.total - stats.matched} unmatched`} testId="stat-matched" />
        <Stat icon={CheckCircle} label="Positive" value={stats.pos} sub={`${stats.convPct}% of matched`} accent testId="stat-positive" />
        <Stat icon={XCircle} label="Negative" value={stats.neg} sub="closed as lost" testId="stat-negative" />
        <Stat icon={CalendarBlank} label="In follow-up" value={stats.pending} sub="awaiting outcome" testId="stat-pending" />
        <Stat icon={CurrencyInr} label="Cost / positive" value={stats.cpp ? `₹${stats.cpp.toLocaleString("en-IN")}` : "—"} sub={`~₹${stats.spend.toLocaleString("en-IN")} est. spend in view`} accent testId="stat-cpp" />
      </div>

      <div>
        <h2 className="font-heading text-lg font-extrabold tracking-tight text-[#0A0D14]">Lead-by-lead assessment <span className="text-xs font-normal text-[#8a90a0] ml-1">{shown.length} of {(rows || []).length} leads</span></h2>
        {rows === null ? (
          <Skeleton className="h-64 rounded-xl mt-3" />
        ) : (
          <div className="mt-3 rounded-xl border border-[#E5E7EB] bg-white overflow-x-auto">
            <table className="w-full text-sm min-w-[860px]">
              <thead>
                <tr className="border-b border-[#E5E7EB] bg-[#FAFAFB] text-left text-[11px] uppercase tracking-wide text-[#8a90a0]">
                  <th className="px-4 py-3 font-bold">Lead</th>
                  <th className="px-4 py-3 font-bold">Created time</th>
                  <th className="px-4 py-3 font-bold">Campaign</th>
                  <th className="px-4 py-3 font-bold">Instant Form</th>
                  <th className="px-4 py-3 font-bold">Sales status</th>
                  <th className="px-4 py-3 font-bold">Source</th>
                  <th className="px-4 py-3 font-bold text-center">Positive?</th>
                </tr>
              </thead>
              <tbody>
                {shown.length === 0 ? (
                  <tr><td colSpan={7} className="px-4 py-8 text-center text-xs text-[#8a90a0]" data-testid="assessment-empty">
                    {(rows || []).length === 0 ? "No Facebook leads synced yet — leads appear here after your next Meta sync." : "No leads match these filters — clear a filter or widen the date range."}
                  </td></tr>
                ) : shown.map((l) => (
                  <tr key={l.id} className="border-b border-[#F3F4F6] last:border-0 hover:bg-[#FAFAFB]" data-testid={`assessment-row-${l.id}`}>
                    <td className="px-4 py-3">
                      <div className="font-bold text-[#0A0D14] max-w-[150px] truncate" title={l.name}>{l.name || "Unknown"}</div>
                      <div className="text-[11px] text-[#8a90a0]">{l.phone || "—"}</div>
                    </td>
                    <td className="px-4 py-3 text-xs text-[#525866] whitespace-nowrap">{fmtDT(l.created_at)}</td>
                    <td className="px-4 py-3"><div className="max-w-[200px] text-xs font-semibold text-[#0A0D14] truncate" title={l.campaign_name}>{l.campaign_name || "—"}</div></td>
                    <td className="px-4 py-3">
                      <div className="max-w-[180px] flex items-center gap-1 text-[11px] text-[#525866]" title={formName(l.form_id)}>
                        <ClipboardText size={12} className="text-[#FF6B35] shrink-0" /><span className="truncate">{formName(l.form_id)}</span>
                      </div>
                    </td>
                    <td className="px-4 py-3"><StatusChip status={l.sales_status} positive={l.positive} /></td>
                    <td className="px-4 py-3">
                      {l.outcome_source === "sheet" ? <span className="text-[10px] font-bold text-[#0F9D58]">SHEET</span>
                        : l.outcome_source === "zoho" ? <span className="text-[10px] font-bold text-[#E42527]">ZOHO</span>
                        : l.outcome_source === "manual" ? <span className="text-[10px] font-bold text-[#0052FF]">MANUAL</span>
                        : <span className="text-[10px] font-bold text-[#c9cdd6]">—</span>}
                    </td>
                    <td className="px-4 py-3 text-center">
                      <PositiveMark value={l.positive} onToggle={() => mark(l)} />
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
        <p className="text-[11px] text-[#8a90a0] mt-2">Cards recalculate live with every filter. Click the mark to override manually: ✓ → ✗ → —. Cost/positive uses each campaign's CPL as the per-lead cost estimate.</p>
      </div>
    </div>
  );
}
